import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import type { ApiError } from '@/core/api';
import * as api from './api';
import type { RecurringExpenseInput } from './types';

export const recurringExpenseKeys = {
  all: ['recurring-expenses'] as const,
  lists: () => [...recurringExpenseKeys.all, 'list'] as const,
  list: (p: api.RecurringExpenseListParams) => [...recurringExpenseKeys.lists(), p] as const,
  summaries: () => [...recurringExpenseKeys.all, 'summary'] as const,
  summary: (p: api.RecurringExpenseListParams) => [...recurringExpenseKeys.summaries(), p] as const,
  detail: (id: string | number) => [...recurringExpenseKeys.all, 'detail', id] as const,
};

export function useRecurringExpenses(params: api.RecurringExpenseListParams = {}) {
  return useQuery({
    queryKey: recurringExpenseKeys.list(params),
    queryFn: () => api.listRecurringExpenses(params),
  });
}

export function useRecurringExpense(id: string | number) {
  return useQuery({
    queryKey: recurringExpenseKeys.detail(id),
    queryFn: () => api.getRecurringExpense(id),
    enabled: Boolean(id),
  });
}

export function useRecurringExpenseSummary(params: api.RecurringExpenseListParams = {}) {
  return useQuery({
    queryKey: recurringExpenseKeys.summary(params),
    queryFn: () => api.getRecurringExpenseSummary(params),
  });
}

function invalidate(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: recurringExpenseKeys.all });
}

export function useCreateRecurringExpense() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: RecurringExpenseInput) => api.createRecurringExpense(input),
    onSuccess: () => {
      invalidate(qc);
      toast.success('Recurring expense created');
    },
    onError: (err: ApiError) => {
      if (!err.fieldErrors) toast.error(err.message);
    },
  });
}

export function useUpdateRecurringExpense(id: string | number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Partial<RecurringExpenseInput>) => api.updateRecurringExpense(id, input),
    onSuccess: () => {
      invalidate(qc);
      toast.success('Recurring expense updated');
    },
    onError: (err: ApiError) => {
      if (!err.fieldErrors) toast.error(err.message);
    },
  });
}

export function useDeleteRecurringExpense() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string | number) => api.deleteRecurringExpense(id),
    onSuccess: () => {
      invalidate(qc);
      toast.success('Recurring expense deleted');
    },
    onError: (err: ApiError) => toast.error(err.message),
  });
}

export function useGenerateRecurringExpensesNow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.generateRecurringExpensesNow(),
    onSuccess: (res) => {
      invalidate(qc);
      toast.success(`Generated ${res.created} expense row(s)`);
    },
    onError: (err: ApiError) => toast.error(err.message),
  });
}
